import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IoIosRefresh } from 'react-icons/io';
import PermissionChecker from '../UI/PermissionChecker/PermissionChecker';
import { PERMISSIONS } from '../../shared/permission';
import { tagService } from '../../services/tags.service';
import { selectTagsTableFilters, setTagsTableFilters } from '../../redux/slices/tableStatesSlice';
import TableGrid from '../UI/Table/TableGrid/TableGrid';
import IconButton from '../UI/IconButton/IconButton';
import Input from '../UI/Input/Input';
import Button from '../UI/Button/Button';
import TagCreateForm from './TagCreateForm/TagCreateForm';
import TagActionsCell from './TagActionsCell/TagActionsCell';
import styles from './Tags.module.css';

function Tags() {
    const dispatch = useDispatch();
    const { t } = useTranslation('common');
    const tagsTableFilters = useSelector(selectTagsTableFilters);

    const [refreshKey, setRefreshKey] = useState(0);
    const [search, setSearch] = useState('');
    const [tableFilters, setTableFilters] = useState({
        search: '',
        page: 0,
        pageSize: tagsTableFilters.pageSize,
    });
    const [tags, setTags] = useState([]);
    const [total, setTotal] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: tagsTableFilters.pageSize,
    });
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    useEffect(() => {
        if (search.length >= 3 || search.length === 0) {
            setTableFilters(prev => ({ ...prev, search }));
        }
    }, [search]);

    const refreshTable = () => {
        setPaginationModel((m) => ({ ...m, page: 0 }));
        setRefreshKey(prev => prev + 1);
    };

    const columns = [
        { field: 'id', headerName: '№', width: 60, flex: 0, sortable: false },
        { field: 'code', headerName: t('tags.code'), width: 200, flex: 1, sortable: false },
        { field: 'name', headerName: t('tags.name'), width: 200, flex: 1, sortable: false },
        {
            field: 'actions',
            headerName: t('tags.actions'),
            width: 120,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <TagActionsCell
                    params={params}
                    onSuccess={refreshTable}
                />
            ),
        }
    ];

    useEffect(() => {
        let alive = true;

        const load = async () => {
            setIsLoading(true);
            try {
                const response = await tagService.getTags({
                    queryParams: {
                        search: tableFilters.search,
                        page: paginationModel.page + 1,
                        limit: paginationModel.pageSize,
                    }
                });
                if (alive) {
                    setTags(response.rows ?? []);
                    setTotal(response.total ?? 0);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (alive) setIsLoading(false);
            }
        };
        load();
        return () => { alive = false; };
    }, [paginationModel.page, paginationModel.pageSize, tableFilters.search, refreshKey]);

    return (
        <PermissionChecker permissions={[PERMISSIONS.TAGS.VIEW]}>
            <div className={styles.container}>
                <div className={styles.tableWrapper}>
                    <div className={styles.headerContainer}>
                        <IconButton onClick={refreshTable} variant="secondary">
                            <IoIosRefresh />
                        </IconButton>
                        <div className={styles.filtersContainer}>
                            <Input
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPaginationModel((m) => ({ ...m, page: 0 }));
                                }}
                                placeholder={t('tags.search_placeholder')}
                                className={styles.input}
                            />
                        </div>
                        <PermissionChecker permissions={[PERMISSIONS.TAGS.CREATE]}>
                            <Button
                                variant="primary"
                                onClick={() => setIsCreateModalOpen(true)}
                                className={styles.createButton}
                            >
                                {t('tags.create')}
                            </Button>
                        </PermissionChecker>
                    </div>
                    <TableGrid
                        noRowsText={t('tags.no_rows')}
                        columns={columns}
                        rows={tags}
                        loading={isLoading}
                        rowCount={total}
                        paginationMode='server'
                        paginationModel={paginationModel}
                        pageSizeOptions={[10, 25, 50]}
                        sortingMode='server'
                        onPaginationModelChange={(model) => {
                            setPaginationModel(model);
                            dispatch(setTagsTableFilters({
                                ...tagsTableFilters,
                                page: model.page,
                                pageSize: model.pageSize
                            }));
                        }}
                        disableRowSelectionOnClick
                        disableColumnMenu
                        disableColumnSort
                    />
                </div>
                <TagCreateForm
                    isOpen={isCreateModalOpen}
                    onClose={() => setIsCreateModalOpen(false)}
                    onSuccess={refreshTable}
                />
            </div>
        </PermissionChecker>
    );
}

export default Tags;
